/**
 * Central hreflang / canonical mapping. Used by the root <head> and both
 * locale sitemaps so every EN/BG pair is linked identically everywhere.
 * Bulgarian is the x-default. Pages that exist in one language only get a
 * single self-referencing hreflang and no x-default.
 */
import { localeFromPath, localizePathFor, stripLocaleFromPath, type Locale } from "@/lib/i18n/I18nProvider";
import { canonicalBlogPath } from "@/content/posts/canonical-overrides";
import { isPostExcludedFromBg } from "@/content/posts/bg-exclusions";

export const SITE_ORIGIN = "https://perceptica.com";

/** EN-side paths that have no Bulgarian version. */
const EN_ONLY_PATHS = new Set(["/company-presentation"]);

export type HreflangLink = { hreflang: "bg" | "en" | "x-default"; href: string };

/** Absolute URL for a locale-correct path ("/" stays "/", "/bg" has no slash). */
export function absoluteUrl(path: string): string {
  return `${SITE_ORIGIN}${path}`;
}

/** Which locales a language-agnostic (EN-side) path exists in. */
export function availableLocales(agnosticPath: string): Locale[] {
  if (EN_ONLY_PATHS.has(agnosticPath)) return ["en"];
  const blogSlug = agnosticPath.match(/^\/blog\/([^/]+)\/?$/)?.[1];
  if (blogSlug && isPostExcludedFromBg(blogSlug)) return ["en"];
  return ["en", "bg"];
}

/** hreflang set for a language-agnostic path. Identical on both locales. */
export function hreflangForAgnostic(agnosticPath: string): HreflangLink[] {
  const locales = availableLocales(agnosticPath);
  const en = absoluteUrl(localizePathFor("en", agnosticPath));
  const bg = absoluteUrl(localizePathFor("bg", agnosticPath));
  if (locales.length === 1) {
    return [{ hreflang: locales[0], href: locales[0] === "bg" ? bg : en }];
  }
  return [
    { hreflang: "bg", href: bg },
    { hreflang: "en", href: en },
    { hreflang: "x-default", href: bg },
  ];
}

/** Canonical + hreflang for the page at `pathname`. */
export function seoLinksForPath(pathname: string) {
  const locale = localeFromPath(pathname);
  const agnostic = canonicalBlogPath(stripLocaleFromPath(pathname));
  const canonical = absoluteUrl(localizePathFor(locale, agnostic));
  const alternates = hreflangForAgnostic(agnostic);
  return { locale, agnostic, canonical, alternates, bilingual: alternates.length > 1 };
}

/** Single-language pages (for reporting / translation backlog). */
export function enOnlyStaticPaths(): string[] {
  return [...EN_ONLY_PATHS];
}
